import { useLocalization } from '@lib/localization';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import { useAddUserMutation, useUpdateUserMutation, type User } from '@store/api/userEndpoints';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUser?: User | null;
}

const addUserSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters'),
  username: z.string()
    .min(3, 'Username must be at least 3 characters'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .refine(
      password => {
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password);
        return (hasUpperCase && hasLowerCase && (hasNumber || hasSpecialChar));
      },
      {
        message: 'Password must include at least 1 uppercase, 1 lowercase, and 1 number or special character',
      }
    ),
  role: z.enum(['admin', 'user']),
});

const updateUserSchema = addUserSchema
  .pick({ name: true, password: true })
  .extend({ password: addUserSchema.shape.password.optional() });

type FieldErrorKeys = 'role' | 'name' | 'username' | 'password';
type FormErrors = { [K in FieldErrorKeys]?: string | null };

const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, selectedUser }) => {
  const [role, setRole] = useState<string>('user');
  const [name, setName] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isUpdateForm, setUpdateForm] = useState<boolean>(false);
  const { t } = useLocalization();

  const [addUser, { isLoading: isAddUserLoading }] = useAddUserMutation();
  const [updateUser, { isLoading: isUpdateLoading }] = useUpdateUserMutation();
  const isLoading = isAddUserLoading || isUpdateLoading;

  const [formErrors, setFormErrors] = useState<FormErrors>({
    role: null,
    name: null,
    username: null,
    password: null
  });

  useEffect(() => {
    const isUpdateForm = !!selectedUser;

    setUpdateForm(isUpdateForm);
    if (isUpdateForm) {
      setName(selectedUser.name);
      setRole(selectedUser.role);
      setUsername(selectedUser.username);
    }
  }, [selectedUser]);

  const handleAddUser = async () => {
    if (isLoading) return;

    const validation = addUserSchema.safeParse({
      role,
      name,
      username,
      password
    });

    if (!validation.success) {
      const zodErrors = validation.error.formErrors.fieldErrors;
      const fieldErrors: FormErrors = {
        role: null,
        name: null,
        username: null,
        password: null
      };

      type ZodErrorKeys = keyof typeof zodErrors;
      (Object.keys(zodErrors) as ZodErrorKeys[]).forEach((key) => {
        if (zodErrors[key]) {
          fieldErrors[key] = zodErrors[key][0];
        }
      });

      setFormErrors(fieldErrors);
      return;
    }

    const result = await addUser(validation.data);

    if (!result.error) handleClose();
  };

  const handleUpdateUser = async () => {
    if (isLoading || !selectedUser) return;

    const validation = updateUserSchema.safeParse({
      name,
      password: password ? password : undefined,
    });

    if (!validation.success) {
      const zodErrors = validation.error.formErrors.fieldErrors;
      const fieldErrors: FormErrors = { name: null, password: null };

      type ZodErrorKeys = keyof typeof zodErrors;
      (Object.keys(zodErrors) as ZodErrorKeys[]).forEach((key) => {
        if (zodErrors[key]) {
          fieldErrors[key] = zodErrors[key][0];
        }
      });

      setFormErrors(fieldErrors);
      return;
    }

    const result = await updateUser({ id: selectedUser.id, ...validation.data });

    if (!result.error) handleClose();
  };

  const handleClose = () => {
    if (isLoading) return;

    clearForm();
    onClose();
  };

  const clearForm = () => {
    setRole('user');
    setName('');
    setPassword('');
    setUsername('');
    setFormErrors({
      role: null,
      name: null,
      username: null,
      password: null
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>{t(`${!isUpdateForm ? 'User_Create' : 'User_Update'}`)}</DialogTitle>
      <DialogContent>
        <form>
          <TextField
            select
            fullWidth
            label={t('Common_Role')}
            margin="normal"
            variant="outlined"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            error={!!formErrors.role}
            helperText={formErrors.role}
            disabled={isUpdateForm || isLoading}
          >
            <MenuItem value="admin">{t('User_Role_Admin')}</MenuItem>
            <MenuItem value="user">{t('User_Role_User')}</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label={t('Common_Name')}
            margin="normal"
            variant="outlined"
            placeholder="Enter name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!formErrors.name}
            helperText={formErrors.name}
            disabled={isLoading}
          />
          <TextField
            fullWidth
            label={t('Common_Username')}
            margin="normal"
            variant="outlined"
            placeholder="Enter username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!formErrors.username}
            helperText={formErrors.username}
            disabled={isUpdateForm || isLoading}
          />
          <TextField
            fullWidth
            label={`${t('Common_Password')}${isUpdateForm ? ` (${t('Common_Optional')})` : ''}`}
            type="password"
            margin="normal"
            variant="outlined"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!formErrors.password}
            helperText={formErrors.password}
            disabled={isLoading}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <div>
            <Button
              onClick={handleClose} color="primary" sx={{ mr: '12px' }} startIcon={<ClearIcon />} disabled={isLoading}>
              {t('Common_Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={!isUpdateForm ? handleAddUser : handleUpdateUser}
              color="primary"
              startIcon={<CheckIcon />}
              disabled={isLoading}
            >
              {t('Common_Submit')}
            </Button>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormModal;
